import React, { useState } from 'react';
import ContactForm from '../ContactForm';

const ContactTab = ({ organisation, currentUser }) => {
  const [currentOfficeIndex, setCurrentOfficeIndex] = useState(0);
  const [viewMode, setViewMode] = useState('carousel'); // 'carousel' or 'grid'

  // Get the additional offices (excluding first office)
  const additionalOffices = organisation.offices?.slice(1) || [];

  const nextOffice = () => {
    if (additionalOffices.length > 1) {
      setCurrentOfficeIndex((prev) => (prev + 1) % additionalOffices.length);
    }
  };

  const prevOffice = () => {
    if (additionalOffices.length > 1) {
      setCurrentOfficeIndex((prev) => (prev - 1 + additionalOffices.length) % additionalOffices.length);
    }
  };

  const goToOffice = (index) => {
    setCurrentOfficeIndex(index);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Contact Information Column */}
      <div className="rounded-lg">
        <h3 className="text-lg font-semibold mb-6">
          Contact Information
          {organisation.offices && organisation.offices.length > 1 && (
            <span className="ml-2 text-sm text-gray-500 font-normal">
              ({organisation.offices.length} offices)
            </span>
          )}
        </h3>
        
        {organisation.offices && organisation.offices.length > 0 ? (
          <div className="space-y-6">
            {/* Additional Offices - Carousel or Grid */}
            {organisation.offices.length > 1 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-800">Additional Office Locations</h4>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewMode('carousel')}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        viewMode === 'carousel' 
                          ? 'bg-indigo-100 text-indigo-800' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Carousel
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        viewMode === 'grid' 
                          ? 'bg-indigo-100 text-indigo-800' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Grid
                    </button>
                  </div>
                </div>

                {viewMode === 'carousel' ? (
                  // Carousel View
                  <div className="relative w-full">
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <div
                        className="flex transition-transform duration-300 ease-in-out"
                        style={{ transform: `translateX(-${currentOfficeIndex * 100}%)` }}
                      >
                        {additionalOffices.map((office, index) => (
                          <div key={office.office_id || index} className="w-full flex-shrink-0 p-4 bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-800">
                                {office.town || `Office ${index + 2}`}
                                {office.office_type === 'head' && (
                                  <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                    Head
                                  </span>
                                )}
                                {office.office_type === 'main' && (
                                  <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                    Main
                                  </span>
                                )}
                              </h5>
                              {office.phone_number && currentUser && (
                                <a
                                  href={`tel:${office.phone_number}`}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                                  title="Call this office"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                </a>
                              )}
                            </div>
                            
                            <div className="text-sm text-gray-600 space-y-1">
                              {office.name && office.name !== office.town && (
                                <p className="font-medium">{office.name}</p>
                              )}
                              {office.address1 && <p>{office.address1}</p>}
                              {office.address2 && <p>{office.address2}</p>}
                              {office.postcode && <p>{office.postcode}</p>}
                              {office.email && (
                                <p>
                                  {currentUser ? (
                                    <a href={`mailto:${office.email}`} className="text-blue-600 hover:text-blue-800">
                                      {office.email}
                                    </a>
                                  ) : (
                                    <span className="text-gray-500 italic">Please sign up to view contact details</span>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Carousel Controls */}
                    {additionalOffices.length > 1 && (
                      <div className="flex items-center justify-between mt-3">
                        <button
                          onClick={prevOffice}
                          className="p-2 rounded-full bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {/* Office counter */}
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">
                            {currentOfficeIndex + 1} of {additionalOffices.length}
                          </span>
                        </div>

                        <button
                          onClick={nextOffice}
                          className="p-2 rounded-full bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  // Grid View
                  <div className="grid grid-cols-1 gap-3">
                    {additionalOffices.map((office, index) => (
                      <div key={office.office_id || index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-sm text-gray-800">
                            {office.town || `Office ${index + 2}`}
                            {office.office_type === 'head' && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                                Head
                              </span>
                            )}
                            {office.office_type === 'main' && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                                Main
                              </span>
                            )}
                          </h5>
                          {office.phone_number && currentUser && (
                            <a
                              href={`tel:${office.phone_number}`}
                              className="p-1 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                              title="Call this office"
                            >
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </a>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-600 space-y-1">
                          {office.name && office.name !== office.town && (
                            <p className="font-medium">{office.name}</p>
                          )}
                          {office.address1 && <p>{office.address1}</p>}
                          {office.postcode && <p>{office.postcode}</p>}
                          <div className="flex items-center justify-between mt-2">
                            {office.phone_number && (
                              <span className="text-blue-600">
                                {currentUser ? office.phone_number : "Please sign up to view contact details"}
                              </span>
                            )}
                            {office.email && (
                              currentUser ? (
                                <a href={`mailto:${office.email}`} className="text-blue-600 hover:text-blue-800">
                                  Email
                                </a>
                              ) : (
                                <span className="text-gray-500 italic text-xs">Please sign up to view contact details</span>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Primary Office Details - Moved Down */}
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-4">
                Main Office
                {organisation.offices[0].office_type === 'head' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    Head Office
                  </span>
                )}
                {organisation.offices[0].office_type === 'main' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                    Main Office
                  </span>
                )}
              </h4>
              
              <div className="space-y-4">
                {/* Address */}
                {(organisation.offices[0].address1 || organisation.offices[0].address2 || organisation.offices[0].address3 || organisation.offices[0].postcode) && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-500 mb-1">Address</p>
                      <div className="text-gray-700 text-sm space-y-1">
                        {organisation.offices[0].address1 && <p>{organisation.offices[0].address1}</p>}
                        {organisation.offices[0].address2 && <p>{organisation.offices[0].address2}</p>}
                        {organisation.offices[0].address3 && <p>{organisation.offices[0].address3}</p>}
                        <p>
                          {organisation.offices[0].town}
                          {organisation.offices[0].postcode && `, ${organisation.offices[0].postcode}`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phone */}
                {organisation.offices[0].phone_number && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-500 mb-1">Phone</p>
                      <a
                        href={`tel:${organisation.offices[0].phone_number}`}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {organisation.offices[0].phone_number}
                      </a>
                    </div>
                  </div>
                )}

                {/* Email */}
                {organisation.offices[0].email && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-500 mb-1">Email</p>
                      <a
                        href={`mailto:${organisation.offices[0].email}`}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {organisation.offices[0].email}
                      </a>
                    </div>
                  </div>
                )}

                {/* Website */}
                {organisation.offices[0].website && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s1.343-9-3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-500 mb-1">Website</p>
                      <a
                        href={organisation.offices[0].website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {organisation.offices[0].website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 italic">No office information available</p>
        )}
      </div>

      {/* Contact Form Column */}
      <div className="rounded-lg">
        <h3 className="text-lg font-semibold mb-6">Send a Message</h3>
        <ContactForm organisation={organisation} />
      </div>
    </div>
  );
};

export default ContactTab; 